import * as React from "react"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { MdArrowForward } from "react-icons/md"
import { Button, Text, StyledModalActions } from "gatsby-interface"
import { netlifyIntegrationConfig as text } from "@modules/locales/default.js"
import LoadingFromExternalWindow from "@modules/ui/components/LoadingFromExternalWindow"
import { useNetlifyAddedSubscription } from "@modules/cdn/netlify/queries.generated"
import { FormattedMessage } from "@modules/locales"

export type ConnectNetlifyFormProps = {
  siteId: string
  siteName: string
  onConnected: (token: string | null) => void
}

export function ConnectNetlifyForm({
  siteId,
  siteName,
  onConnected,
}: ConnectNetlifyFormProps) {
  const [isWindowOpened, openWindow] = useOpenNetlifyWindow(siteId)
  const [setError, errorAlert] = useTriggerErrorAlert()

  useNetlifyConnect(siteId, { onConnected, onError: setError })

  if (isWindowOpened) {
    return (
      <LoadingFromExternalWindow
        css={{ minHeight: `auto` }}
        message={text.messages.authorizingNetlify}
        onClick={openWindow}
      />
    )
  }

  return (
    <React.Fragment>
      <Text>
        <FormattedMessage<"siteName", "strong">
          message={text.headers.connectToSite}
          values={{ siteName }}
          tags={{
            strong: function(content) {
              return <strong>{content}</strong>
            },
          }}
        />
      </Text>
      {errorAlert}
      <StyledModalActions>
        <div />
        <Button onClick={openWindow} rightIcon={<MdArrowForward />}>
          {text.actions.connect}
        </Button>
      </StyledModalActions>
    </React.Fragment>
  )
}

function useOpenNetlifyWindow(siteId: string) {
  const [isWindowOpened, setIsWindowOpened] = React.useState<boolean>(false)

  const openWindow = () => {
    setIsWindowOpened(true)

    window.open(
      `${process.env.GATSBY_DASHBOARD_AUTHENTICATION_URL}/oauth/netlify?siteId=${siteId}`,
      `_blank`,
      `height=600,width=700`
    )
  }

  return [isWindowOpened, openWindow] as const
}

function useNetlifyConnect(
  siteId: string,
  {
    onConnected,
    onError,
  }: {
    onConnected: (token: string | null) => void
    onError: (error: Error) => void
  }
) {
  const { error } = useNetlifyAddedSubscription({
    variables: {
      siteId,
    },
    onSubscriptionData: data =>
      onConnected(
        data?.subscriptionData?.data?.netlifyIntegrationAdded ?? null
      ),
  })

  React.useEffect(() => {
    if (error) {
      onError(error)
    }
  }, [error, onError])
}
